import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormFieldProps, RadioGroupField } from '@brighte/form-components';
import { FinancePurchaseType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { RepaymentOption } from '@graphql/generated/graphql';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { RadioCard } from '@spark-web/radio';
import { Text } from '@spark-web/text';
import { getTermLabel, withSingleRepaymentTerm } from '@steps/FinancePurchase/utils/helpers';
import { FINANCE_PURCHASE, KNOWN_ERROR_MESSAGES } from '@utils';

type Props<TFieldValues extends FinancePurchaseType> = {
  repayments: RepaymentOption[] | [];
  isMultiCategory: boolean;
  isCashApplication: boolean;
} & Omit<FormFieldProps<TFieldValues>, 'label'> &
  Pick<UseFormReturn<TFieldValues>, 'setValue' | 'register' | 'control'>;

function RepaymentTerms({
  name,
  control,
  repayments,
  setValue,
  register,
  isMultiCategory,
  isCashApplication,
}: Props<FinancePurchaseType>) {
  const selectedRepayment = useWatch({ control, name });

  const shouldHideRepayments = withSingleRepaymentTerm(repayments) || isCashApplication;

  const handleRetry = () => {
    datadogRum.addAction('Repayment term was not loaded : Retry button clicked');
    window.location.reload();
  };

  useEffect(() => {
    if (shouldHideRepayments) {
      setValue(name, repayments[0]?.termMonths);
    } else if (selectedRepayment) {
      setValue(name, selectedRepayment);
    } else {
      setValue(name, undefined);
    }
  }, [isCashApplication]);

  if (repayments.length === 0) {
    return (
      <Alert tone="critical">
        <Text>{KNOWN_ERROR_MESSAGES.REPAYMENT_SUMMARY_NOT_FOUND}</Text>
        <Box paddingY="small">
          <Button tone="neutral" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      </Alert>
    );
  }

  if (shouldHideRepayments) {
    return <input type="hidden" {...register(name)} data-testid={name} />;
  }

  return (
    <RadioGroupField label={FINANCE_PURCHASE.HEADING_FINANCE_TERMS} name={name} control={control}>
      {repayments
        .map(({ repaymentAmount, termMonths }: RepaymentOption) => {
          return (
            <RadioCard
              key={termMonths}
              value={`${termMonths}`}
              description={!isMultiCategory ? `from $${repaymentAmount} per fortnight` : ''}
            >
              <Text weight="semibold">{getTermLabel(repayments, termMonths)}</Text>
            </RadioCard>
          );
        })
        .reverse()}
    </RadioGroupField>
  );
}

export { RepaymentTerms };
